import React, { useEffect, useState, useContext } from 'react';
import axios from 'axios';
import { API, AuthContext } from '../App';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Plus, StickyNote, Bell, Pin, Trash2, CheckCircle2, Clock, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import { useT } from '../contexts/LanguageContext';

const COLORS = [
  { v: 'amber',   c: 'bg-amber-50 border-amber-200 text-amber-900' },
  { v: 'sky',     c: 'bg-sky-50 border-sky-200 text-sky-900' },
  { v: 'emerald', c: 'bg-emerald-50 border-emerald-200 text-emerald-900' },
  { v: 'rose',    c: 'bg-rose-50 border-rose-200 text-rose-900' },
  { v: 'violet',  c: 'bg-violet-50 border-violet-200 text-violet-900' },
  { v: 'slate',   c: 'bg-slate-50 border-slate-200 text-slate-900' },
];
const colorClass = (v) => (COLORS.find(x => x.v === v) || COLORS[0]).c;

const PRIO = {
  high:   'bg-rose-100 text-rose-800 border-rose-200',
  normal: 'bg-amber-100 text-amber-800 border-amber-200',
  low:    'bg-slate-100 text-slate-700 border-slate-200',
};

export default function NotesReminders() {
  const { user } = useContext(AuthContext);
  const t = useT();
  const [tab, setTab] = useState('notes');

  return (
    <Layout>
      <div data-testid="notes-page" className="space-y-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground flex items-center gap-2">
            <StickyNote className="w-6 h-6 text-amber-600" />{t('nav.notes')}
          </h1>
          <p className="text-sm text-muted-foreground">{t('notes.subtitle') === 'notes.subtitle' ? `Personal scratch pad — only visible to ${user?.email || 'you'}.` : t('notes.subtitle')}</p>
        </div>
        <div className="flex gap-2">
          <button data-testid="tab-notes" onClick={() => setTab('notes')} type="button" className={`px-4 py-2 rounded-full text-sm font-semibold inline-flex items-center gap-2 transition ${tab === 'notes' ? 'bg-amber-500 text-slate-900 shadow' : 'bg-white border border-slate-200 text-slate-700 hover:border-amber-300'}`}><StickyNote className="w-4 h-4" />{t('tab.notes')}</button>
          <button data-testid="tab-reminders" onClick={() => setTab('reminders')} type="button" className={`px-4 py-2 rounded-full text-sm font-semibold inline-flex items-center gap-2 transition ${tab === 'reminders' ? 'bg-amber-500 text-slate-900 shadow' : 'bg-white border border-slate-200 text-slate-700 hover:border-amber-300'}`}><Bell className="w-4 h-4" />{t('tab.reminders')}</button>
        </div>
        {tab === 'notes' ? <NotesTab /> : <RemindersTab />}
      </div>
    </Layout>
  );
}

// ============= NOTES TAB =============
function NotesTab() {
  const [items, setItems] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [draft, setDraft] = useState({ title: '', body: '', color: 'amber', pinned: false });
  const [editing, setEditing] = useState(null);

  const fetchItems = () => axios.get(`${API}/notes`, { withCredentials: true }).then(r => setItems(r.data || [])).catch(() => {});
  useEffect(() => { fetchItems(); }, []);

  const create = async () => {
    if (!draft.title && !draft.body) return toast.error('Add a title or body');
    try {
      await axios.post(`${API}/notes`, draft, { withCredentials: true });
      toast.success('Note added'); setShowNew(false); fetchItems();
      setDraft({ title: '', body: '', color: 'amber', pinned: false });
    } catch { toast.error('Save failed'); }
  };

  const update = async (id, patch) => {
    try { await axios.put(`${API}/notes/${id}`, patch, { withCredentials: true }); fetchItems(); }
    catch { toast.error('Update failed'); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete note?')) return;
    try { await axios.delete(`${API}/notes/${id}`, { withCredentials: true }); fetchItems(); }
    catch { toast.error('Delete failed'); }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-sm text-muted-foreground">{items.length} note{items.length !== 1 ? 's' : ''}</p>
        <Dialog open={showNew} onOpenChange={setShowNew}>
          <DialogTrigger asChild>
            <Button data-testid="new-note-btn" className="bg-amber-500 hover:bg-amber-600 text-slate-900"><Plus className="w-4 h-4 mr-1" />New Note</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>New Note</DialogTitle></DialogHeader>
            <div className="space-y-3 mt-2">
              <Input data-testid="note-title-input" value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} placeholder="Title" />
              <Textarea data-testid="note-body-input" rows={5} value={draft.body} onChange={(e) => setDraft({ ...draft, body: e.target.value })} placeholder="Write something…" />
              <div className="flex items-center gap-2 flex-wrap">
                {COLORS.map(c => <button key={c.v} type="button" onClick={() => setDraft({ ...draft, color: c.v })} className={`w-7 h-7 rounded-full border-2 ${c.c} ${draft.color === c.v ? 'ring-2 ring-amber-500' : ''}`} />)}
              </div>
              <Button data-testid="submit-note-btn" onClick={create} className="w-full bg-amber-500 hover:bg-amber-600 text-slate-900">Save</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {items.length === 0 ? <Card className="p-12 text-center text-muted-foreground"><StickyNote className="w-10 h-10 mx-auto text-slate-300 mb-2" />No notes yet</Card> : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
          {items.map(n => (
            <Card key={n.id} data-testid={`note-${n.id}`} className={`p-4 border-2 relative ${colorClass(n.color)}`}>
              <button type="button" onClick={() => update(n.id, { pinned: !n.pinned })} className={`absolute top-2 right-2 ${n.pinned ? 'text-amber-700' : 'text-slate-400'}`}>
                <Pin className={`w-4 h-4 ${n.pinned ? 'fill-amber-400' : ''}`} />
              </button>
              {n.title && <div className="font-semibold mb-2 pr-6">{n.title}</div>}
              <div className="text-sm whitespace-pre-wrap break-words">{n.body}</div>
              <div className="flex justify-end gap-1 mt-3 pt-2 border-t border-current/10">
                <Button size="sm" variant="ghost" onClick={() => setEditing(n)}><Pencil className="w-3.5 h-3.5" /></Button>
                <Button size="sm" variant="ghost" onClick={() => remove(n.id)}><Trash2 className="w-3.5 h-3.5 text-red-500" /></Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      {editing && (
        <Dialog open onOpenChange={() => setEditing(null)}>
          <DialogContent>
            <DialogHeader><DialogTitle>Edit Note</DialogTitle></DialogHeader>
            <div className="space-y-3 mt-2">
              <Input value={editing.title || ''} onChange={(e) => setEditing({ ...editing, title: e.target.value })} placeholder="Title" />
              <Textarea rows={5} value={editing.body || ''} onChange={(e) => setEditing({ ...editing, body: e.target.value })} />
              <div className="flex items-center gap-2 flex-wrap">
                {COLORS.map(c => <button key={c.v} type="button" onClick={() => setEditing({ ...editing, color: c.v })} className={`w-7 h-7 rounded-full border-2 ${c.c} ${editing.color === c.v ? 'ring-2 ring-amber-500' : ''}`} />)}
              </div>
              <Button onClick={async () => { await update(editing.id, { title: editing.title, body: editing.body, color: editing.color }); setEditing(null); }} className="w-full bg-amber-500 hover:bg-amber-600 text-slate-900">Save</Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

// ============= REMINDERS TAB =============
function RemindersTab() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState('pending');
  const [showNew, setShowNew] = useState(false);
  const [draft, setDraft] = useState({ title: '', notes: '', due_at: '', priority: 'normal' });

  const fetchItems = () => {
    const url = filter === 'all' ? `${API}/reminders` : `${API}/reminders?status=${filter}`;
    axios.get(url, { withCredentials: true }).then(r => setItems(r.data || [])).catch(() => {});
  };
  useEffect(() => { fetchItems(); /* eslint-disable-next-line */ }, [filter]);

  const create = async () => {
    if (!draft.title || !draft.due_at) return toast.error('Title and due date required');
    try {
      const due_iso = new Date(draft.due_at).toISOString();
      await axios.post(`${API}/reminders`, { ...draft, due_at: due_iso }, { withCredentials: true });
      toast.success('Reminder set'); setShowNew(false); fetchItems();
      setDraft({ title: '', notes: '', due_at: '', priority: 'normal' });
    } catch { toast.error('Save failed'); }
  };

  const setStatus = async (id, status) => {
    try { await axios.put(`${API}/reminders/${id}`, { status }, { withCredentials: true }); fetchItems(); }
    catch { toast.error('Update failed'); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete reminder?')) return;
    try { await axios.delete(`${API}/reminders/${id}`, { withCredentials: true }); fetchItems(); }
    catch { toast.error('Delete failed'); }
  };

  const now = Date.now();

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap justify-between items-center gap-2">
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger data-testid="reminder-filter" className="w-44"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="done">Done</SelectItem>
            <SelectItem value="snoozed">Snoozed</SelectItem>
            <SelectItem value="all">All</SelectItem>
          </SelectContent>
        </Select>
        <Dialog open={showNew} onOpenChange={setShowNew}>
          <DialogTrigger asChild>
            <Button data-testid="new-reminder-btn" className="bg-amber-500 hover:bg-amber-600 text-slate-900"><Plus className="w-4 h-4 mr-1" />New Reminder</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>New Reminder</DialogTitle></DialogHeader>
            <div className="space-y-3 mt-2">
              <div><Label>Title *</Label><Input data-testid="reminder-title-input" value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} /></div>
              <div><Label>Due *</Label><Input data-testid="reminder-due-input" type="datetime-local" value={draft.due_at} onChange={(e) => setDraft({ ...draft, due_at: e.target.value })} /></div>
              <div><Label>Priority</Label>
                <Select value={draft.priority} onValueChange={(v) => setDraft({ ...draft, priority: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Notes</Label><Textarea rows={3} value={draft.notes} onChange={(e) => setDraft({ ...draft, notes: e.target.value })} /></div>
              <Button data-testid="submit-reminder-btn" onClick={create} className="w-full bg-amber-500 hover:bg-amber-600 text-slate-900">Save</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {items.length === 0 ? <Card className="p-12 text-center text-muted-foreground"><Bell className="w-10 h-10 mx-auto text-slate-300 mb-2" />Nothing here</Card> : (
        <div className="space-y-2">
          {items.map(r => {
            const due = r.due_at ? new Date(r.due_at).getTime() : 0;
            const overdue = r.status === 'pending' && due < now;
            return (
              <Card key={r.id} data-testid={`reminder-${r.id}`} className={`p-4 flex items-center gap-3 ${overdue ? 'border-l-4 border-l-rose-500' : ''}`}>
                <button type="button" data-testid={`reminder-toggle-${r.id}`} onClick={() => setStatus(r.id, r.status === 'done' ? 'pending' : 'done')} className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${r.status === 'done' ? 'bg-emerald-500 border-emerald-500' : 'border-slate-300'}`}>
                  {r.status === 'done' && <CheckCircle2 className="w-4 h-4 text-white" />}
                </button>
                <div className="flex-1 min-w-0">
                  <div className={`font-semibold truncate ${r.status === 'done' ? 'line-through text-slate-400' : 'text-slate-900'}`}>{r.title}</div>
                  <div className="text-xs text-muted-foreground flex items-center gap-2 flex-wrap">
                    <span className={`inline-flex items-center gap-1 ${overdue ? 'text-rose-700 font-semibold' : ''}`}><Clock className="w-3 h-3" />{r.due_at ? new Date(r.due_at).toLocaleString() : ''}</span>
                    <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${PRIO[r.priority] || PRIO.normal}`}>{r.priority}</span>
                    {overdue && <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-rose-100 text-rose-800 border-rose-200">Overdue</span>}
                  </div>
                  {r.notes && <p className="text-sm text-muted-foreground mt-1">{r.notes}</p>}
                </div>
                <Button size="sm" variant="ghost" className="text-red-500" onClick={() => remove(r.id)}><Trash2 className="w-3.5 h-3.5" /></Button>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
